import React, { Component, Fragment, type JSX } from 'react';
import { Field, FormikProps, FormikValues } from 'formik';
import { CheckoutOrderType, FormValues } from '../Checkout.interface';
import { post } from '../../../util/ApiWrapper';

type Total = CalculationResponse['total'];

interface CouponProps {
  form: FormikProps<FormValues>;
  orderType: CheckoutOrderType;
  setTotal: (total: Total) => void;
}

interface CouponState {
    displayForm: boolean;
    errorMessage?: { discount_code: string[]; } | string;
    successMessage?: string;
    validating: boolean;
}

export interface CalculationResponse {
  total?: {
    discountApplied: boolean;
    amountSubTotal: string;
    amountTotal: string;
    amountDiscount: string;
    appliedCode: string;
    errorMessage?: string|string[]; // only set if discountApplied = false
  };
  error?: string;
}

/**
 * This component contains the coupon field, it toggles the
 * field and submits the value to the backend. When the ajax
 * call completes, the component calls the setTotal() method
 * which got passed by the parent component, to set the total
 * values in the state of the parent component. This way the
 * information in the CartTotal component can be updated as well.
 */
export class CouponForm extends Component<CouponProps, CouponState> {

  public state: CouponState = {
    validating: false,
    displayForm: false,
  };

  /**
   * When the "Heb je een kortingscode?" is clicked
   * toggle the form.
   */
  public toggleCouponField(): void {
    this.setState({
      ...this.state,
      displayForm: true,
    });
  }

  /**
   * Send the coupon code to the backend. Show a loader
   * while validating the code.
   */
  public async validateCoupon(value: string = ''): Promise<void> {
    const { orderType }: CouponProps = this.props;

    this.setState({
      ...this.state,
      successMessage: undefined,
      errorMessage: undefined,
      validating: true,
    });

    try {
      const response: CalculationResponse = await post<CalculationResponse>({
        controller: 'Coupon',
        method: 'validateCouponCode',
        data: {
          cartSession: orderType === CheckoutOrderType.SCHOOL
            ? 'InSchool'
            : 'ReservationEXT',
          couponCode: value,
        },
      });

      this.displayDiscount(response, value);
    } catch (e) {
      // the backend returns validation errors in a 200.
      // So when something goes wrong display a generic message.
      this.setState({
        ...this.state,
        errorMessage: 'De kortingscode kon niet worden gevalideerd.',
      });
    }
  }

  /**
   * Display the feedback from the backend, this can either be
   * a success- or a error message. This method also call the
   * this.props.setTotal() method to pass the cart total to the
   * parent component.
   */
  private displayDiscount(response: CalculationResponse, couponCode: string): void {
    const { total, error } = response;

    if (!total && !!error) {
      this.setState({
        ...this.state,
        errorMessage: error,
        validating: false,
      });
      return;
    }

    this.setState({
      ...this.state,
      validating: false,
    });

    if (total.discountApplied) {
      const totalWithCode = { ...total, appliedCode: couponCode };
      this.props.setTotal(totalWithCode);

      this.setState({
        ...this.state,
        successMessage: '<span>De kortingscode is toegepast,<br/>'
          + `uw korting: <b>&euro; ${total.amountDiscount}</b></span>`,
      });
    } else if (total.errorMessage) {
      this.setState({
        ...this.state,
        errorMessage: Array.isArray(total.errorMessage)
          ? total.errorMessage[0]
          : total.errorMessage,
      });
    }
  }

  /**
   * The input field is initially hidden. Only when clicking the
   * "Heb je een kortingscode?" link the form will be toggled.
   */
  public render(): JSX.Element {
    const { validating, displayForm, errorMessage, successMessage }: CouponState = this.state;
    const { values }: FormikValues = this.props.form;

    const shouldShowForm: boolean = displayForm || !!values.couponCode;

    return (
      <Fragment>
        {!shouldShowForm
        && <a onClick={(): void => this.toggleCouponField()}
          className="coupon__toggle">
          Heb je een kortingscode?
        </a>}

        <div className={`coupon__form ${shouldShowForm ? 'show' : ''}`}>
          <Field type="text"
            name="couponCode"
            className="form-control bem-input-field mb-1"
            placeholder="Kortingscode..."/>
          <button disabled={validating || !values.couponCode}
            onClick={(): Promise<void> => this.validateCoupon(values.couponCode)}
            className="btn btn-block btn-default">
              Controleren {validating && <i className="fa fa-circle-o-notch fa-spin"/>}
          </button>
          {errorMessage && <div className="coupon__response coupon__response--error">
              { JSON.stringify(errorMessage) }
          </div>}
          {successMessage && <div className="coupon__response coupon__response--success"
            dangerouslySetInnerHTML={{ __html: successMessage }}/>}
        </div>
      </Fragment>
    );
  }

}
